import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../lib/auth.context'
import Background from '../background'
import StepsIndicator from '../steps-indicator'
import { InputOTP } from '../input-otp'
import { EnrollmentSuccess } from './totp-enrollment-success'

export function TOTPEnrollment() {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const { toast } = useToast()
  const { t } = useTranslation()
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    const setupTOTP = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user?.email) throw new Error('User email not found')

        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `${t('title')} - ${user.email}`,
        })

        if (error) throw error

        if (data.totp) {
          setQrCode(data.totp.qr_code)
          setSecret(data.totp.secret)
          setFactorId(data.id)
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to setup TOTP'
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    setupTOTP()
  }, [toast, t])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId,
      })

      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode,
      })

      if (verifyError) throw verifyError

      toast({
        title: 'Success',
        description: 'TOTP device enrolled successfully',
      })

      // Refresh the session to ensure we have the latest token
      const {
        data: { session: newSession },
        error: sessionError,
      } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      // Ensure we have a valid session before redirecting
      if (!newSession?.access_token) {
        throw new Error('Failed to initialize session after TOTP enrollment')
      }

      setShowSuccess(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full min-h-screen flex items-center justify-center relative">
      {/* add the fanfair logo to the top left of the page */}
      <div className="absolute top-6 left-6 pt-4">
        <img src="/fanfair.png" alt="FanFair" className="h-20" />
      </div>
      <div className="w-full max-w-5xl  rounded-2xl overflow-hidden">
        <div>
          {/* Left Panel - Marketing Content */}
          {/* Right Panel - Login Form */}
          <div className="w-full flex items-center justify-center lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto" />
              </div>
              <div className="!mt-0">
                {!showSuccess ? (
                  <form onSubmit={handleVerify}>
                    <div className="space-y-2">
                      {/*<h1 className="text-2xl font-medium text-white">You need an authentication app to scan the QR below</h1>*/}
                      {/*<p className="text-slate-400 font-light text-center mb-3">
                        (Google Authenticator, Microsoft Authenticator, etc).
                      </p>*/}
                    </div>
                    <div className="flex flex-col w-full gap-3 items-center">
                      {formError && (
                        <Alert variant="destructive" className="">
                          <AlertDescription>{formError}</AlertDescription>
                        </Alert>
                      )}
                      <div className='text-center'>
                        <h1 className="font-lato text-[23px] font-medium text-black">
                          Set Up Two-Factor <br/> Authentication</h1>
                      </div>
                      <div className="flex flex-col mt-1 items-center bg-white p-1 rounded-xl h-60 w-60">
                        {qrCode && <img src={qrCode} alt="QR Code" className="w-full h-full" />}
                      </div>
                      <div className='flex flex-col items-center gap-2'>
                        <span className="text-xs font-lato text-black font-medium">
                          Manual entry code:
                        </span>
                        <div className="bg-[#F0EFEB] font-lato text-sm text-black font-medium border border-gray-300 rounded-md p-2">
                          {secret}
                        </div>
                      </div>
                      <div className='text-center'>
                        <span className="font-lato text-sm text-[#BB33FF]">
                          Scan the QR code or enter the secret key manually in your authenticator app
                        </span>
                      </div>
                      <Input type="text" placeholder="Enter verification code" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} />
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="font-lato font-medium w-full bg-[#570393] hover:bg-[#570393]/90 text-white"
                      >
                        {isLoading ? 'Verifying...' : 'Verify and Complete Setup'}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <EnrollmentSuccess />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 right-6">
        <img src="/green-tickets.png" alt="Green Tickets" className="h-[300px]" />
      </div>
    </div>
  )
}
